/**
 * Auth context — single source of truth for "who is logged in".
 *
 * Hydrates from localStorage on mount, refreshes from /me on auth
 * changes (so subscription state is always fresh after redirect from
 * checkout / cancel).
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Auth, api, type UserMe } from './api';

interface AuthState {
  user: UserMe | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<AuthState>({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserMe | null>(Auth.getUser());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!Auth.getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const fresh = await api.me();
      Auth.setUser(fresh);
      setUser(fresh);
    } catch {
      Auth.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    Auth.clear();
    setUser(null);
    window.location.href = '/';
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <Ctx.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
